import { Button, FieldInput, Meta, theme } from 'aimo-ui';
import SuccessIndicator from 'components/common/SuccessIndicator';
import Icon from 'components/icons/Icon';
import StaffMemberSignInPrompt from 'components/park/StaffMemberSignInPrompt';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useVerifyCustomerWithHsaId from 'hooks/useVerifyCustomerWithHsaId';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { usePermitStore } from 'stores/permitStore';
import { useUiStore } from 'stores/uiStore';
import styled from 'styled-components/native';
import { StyledWrapper } from 'styles/ContainerStyles';

const ContentWrapper = styled(StyledWrapper)`
  padding: 0 20px;
`;

const InputWrapper = styled(StyledWrapper)`
  margin: 24px 0 12px;
`;

const ButtonWrapper = styled(StyledWrapper)`
  margin-bottom: 12px;
`;

const ErrorContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  margin-bottom: 12px;
`;

const ErrorText = styled(Meta)`
  color: ${theme.colors.aimoUiRed};
  margin-left: 8px;
`;

const PermitHsaModalContent: React.FC = () => {
  const { t } = useTranslation();
  const { isLoggedIn } = useIsLoggedIn();
  const { verifyCustomer, error } = useVerifyCustomerWithHsaId();
  const { hsaIdState, setHsaIdCode, setCustomerVerificationStatus } =
    usePermitStore();
  const { hsaIdCode, customerVerificationStatus } = hsaIdState;
  const isLoading = customerVerificationStatus === 'loading';

  useEffect(() => {
    setCustomerVerificationStatus('idle');
  }, []);

  const handleVerify = () => {
    setCustomerVerificationStatus('loading');
    if (hsaIdCode.trim()) {
      verifyCustomer({
        variables: {
          input: {
            externalVerificationType: 'HSA',
            verificationSubjectId: hsaIdCode.trim(),
            verificationMethod: 'USER_INTERACTIVE',
          },
        },
      });
    }
  };

  const handleClose = () => {
    useUiStore.getState().setSheetModal('');
  };

  if (!isLoggedIn) {
    return <StaffMemberSignInPrompt testID="permit-hsa-login" />;
  }

  if (customerVerificationStatus === 'success') {
    return (
      <View>
        <StyledWrapper mt={30} mb={0}>
          <SuccessIndicator
            message={t('longTermParking.staffMemberPermit.successMessage')}
            testID="tag-register-success"
          />
        </StyledWrapper>
        <StyledWrapper mb={60} mt={0}>
          <Meta>
            {t('longTermParking.staffMemberPermit.successDescription')}
          </Meta>
        </StyledWrapper>
        <Button
          size="l"
          onPress={handleClose}
          text={t('button.close')}
          testID="continue-permission-button"
          buttonType="secondary"
        />
      </View>
    );
  }

  return (
    <ContentWrapper mb={0}>
      <StyledWrapper mb={24} center>
        <Meta center color={theme.colors.gray300}>
          {t('longTermParking.staffMemberPermit.hsa.description')}
        </Meta>
      </StyledWrapper>

      <InputWrapper>
        <FieldInput
          testID="charger-number-input"
          title={t('longTermParking.staffMemberPermit.hsa.idCode')}
          value={hsaIdCode}
          onChangeText={setHsaIdCode}
          placeholder={t('longTermParking.staffMemberPermit.hsa.placeholder')}
          isSheet
          disabled={isLoading}
        />
      </InputWrapper>

      {error && (
        <ErrorContainer>
          <Icon name="Info-Filled" color={theme.colors.aimoUiRed} size="m" />
          <ErrorText>
            {t('longTermParking.staffMemberPermit.hsa.verifyFailed')}
          </ErrorText>
        </ErrorContainer>
      )}

      <ButtonWrapper>
        <Button
          testID="next-button"
          size="l"
          text={t('longTermParking.staffMemberPermit.hsa.verify')}
          onPress={handleVerify}
          disabled={!hsaIdCode.trim() || isLoading}
          loading={isLoading}
        />
      </ButtonWrapper>

      <ButtonWrapper mb={0}>
        <Button
          testID="cancel-button"
          size="l"
          buttonType="tertiary"
          text={t('button.cancel')}
          onPress={handleClose}
        />
      </ButtonWrapper>
    </ContentWrapper>
  );
};

export default PermitHsaModalContent;
